import { Data } from '@/bootstrap/data';
import * as selectors from '../../../selectors/selectors';
import { SearchResult } from './SearchResult';
import { ConfigAndDataContext } from '@/state/context/ConfigAndDataContext';
import { useCallback, useContext, useMemo } from 'react';
import { Vegobjekttype } from '@/domain/vegobjekter/Vegobjekttype';
import { Fylke, Kommune, Kontraktsomrade, Land, Riksvegrute } from '@/domain/omrader';
import { Vegkategori } from '@/domain/Vegkategori';
import { Stedsnavn } from '@/server/responses';
import formatLocationDetails from '@/components/avgrensning/search/dropdown/formatLocationDetails';
import {
    createSearchSuggestionCoordinate,
    createSearchSuggestionHistoricalVegreferanse,
    createSearchSuggestionKommune,
    createSearchSuggestionLinkQuery,
    createSearchSuggestionPolygon,
    createSearchSuggestionRiksvegrute,
    createSearchSuggestionVegobjekt,
    createSearchSuggestionVegobjekttype,
    createSearchSuggestionVegsystemreferanse,
    createSearchSuggestionVegsystemreferanseFromCoordinate,
    createSearchSuggestionVegsystemreferanseFromLink,
    SearchSuggestion,
    useSearchSuggestion,
} from '@/components/avgrensning/search/useSearchSuggestion';
import {
    addFylkeToSearch,
    addKommuneToSearch,
    addKontraktsomradeToSearch,
    addLandToSearch,
    addRiksvegruteToSearch,
    addVSRToSearch,
} from '@/actions/omraderActions';
import { addVegobjekttypeToSearch } from '@/actions/vegobjekterActions';
import { loadMorePlaceNames } from '@/actions/searchActions';
import { getLocationIconName } from '@/components/avgrensning/search/dropdown/locationTypeIconName';
import { setPlacenameMarker } from '@/actions/overlayActions';
import { DropdownItemData } from '@/components/avgrensning/search/dropdown/DropdownItem';
import { Dispatch, useDispatch, useSelector } from '@/state/store';
import { stateUser } from '@/selectors/selectors';
import { parseLegacyVegreferanse } from '@/utils/LegacyVegreferanseParser';
import { createVegrefParser } from '@/utils/VegrefParser';

export const navnFilter =
    (searchText: string) => (o: { navn?: string; nummer?: number | string | null }) =>
        `${o?.nummer}${o.navn?.toLowerCase()}`.includes(searchText?.toLowerCase());

const filterValues = (data: Data, searchText: string): SearchResult => {
    if (searchText.length === 0) {
        return new SearchResult();
    }
    const isNumber = !isNaN(Number(searchText));
    const filter = navnFilter(searchText);
    const comparator = isNumber
        ? prefixComparator<{ nummer: number | null }>(searchText, v =>
              String(v.nummer ?? '')
          )
        : prefixComparator<{ navn: string }>(searchText, v => v.navn);

    return new SearchResult(
        data.datakatalog.vegobjekttyper
            .filter(filter)
            .sort(prefixComparator(searchText, v => v.navn)),
        data.fylker.filter(filter).sort(comparator),
        data.kommuner.filter(filter).sort(comparator),
        data.kontraktsomrader
            .filter(filter)
            .sort(prefixComparator(searchText, v => v.navn)),
        data.riksvegruter.filter(filter).sort(prefixComparator(searchText, v => v.navn)),
        data.land.filter(filter).sort(prefixComparator(searchText, v => v.navn)),
        data.vegkategorier.filter(filter).sort(prefixComparator(searchText, v => v.navn))
    );
};

export const useSearchResult = (searchText: string): DropdownItemData[] => {
    const dispatch = useDispatch();
    const { data, config } = useContext(ConfigAndDataContext);
    const { isLoggedIn } = useSelector(stateUser);
    const placeNames = useSelector(selectors.statePlacenames);
    const suggestion = useSearchSuggestion(searchText);
    const searchResult = useMemo(
        () => filterValues(data, searchText),
        [data, searchText]
    );
    const fylkeByNumber = useCallback(
        (county: number) => data.fylker.find(f => f.nummer === county),
        [data]
    );

    const dropdownItems: DropdownItemData[] = useMemo(() => {
        const groupedByCategory = [
            ...suggestion.map(s => suggestionToEntry(s, dispatch)),
            ...searchResult.vegobjekttyper.map(v =>
                vegobjekttypeToEntry(v, dispatch, isLoggedIn)
            ),
            ...searchResult.land.map(l => landToEntry(l, dispatch)),
            ...searchResult.fylker.map(f => fylkeToEntry(f, dispatch)),
            ...searchResult.kommuner.map(k => kommuneToEntry(k, dispatch, fylkeByNumber)),
            ...searchResult.vegkategorier.map(v => vegkategoriToEntry(v, dispatch)),
            ...searchResult.riksvegruter.map(r => riksvegruteToEntry(r, dispatch)),
            ...searchResult.kontraktsomrader.map(k =>
                kontraktsomradeToEntry(k, dispatch)
            ),
            ...(placeNames?.placeNames ?? []).map(s => stedsnavnToEntry(s, dispatch)),
        ].reduce(
            (all, entry) => {
                if (!entry.category) {
                    return all;
                }

                return {
                    ...all,
                    [entry.category]: [...(all[entry.category] || []), entry],
                };
            },
            {} as Record<string, DropdownItemData[]>
        );

        const result = Object.entries(groupedByCategory)
            .filter(([, entries]) => entries.length > 0)
            .flatMap(([category, data]) => [
                {
                    title: category,
                    header: true,
                    subtitle: `(${data.length} treff)`,
                    disabled: true,
                },
                ...data,
            ]);

        // Add "Load more" button for place names if there are more results
        if (
            placeNames?.placeNames &&
            placeNames.placeNames.length % config.placenames_page_size === 0
        ) {
            const headerIdx = result.findIndex(
                item => item.title === 'Stedsnavn' && item.header
            );
            if (headerIdx !== -1) {
                const insertIdx = result.findIndex(
                    (item, idx) => idx > headerIdx && item.header
                );
                const nextPage =
                    Math.trunc(
                        placeNames.placeNames.length / config.placenames_page_size
                    ) + 1;

                result.splice(insertIdx === -1 ? result.length : insertIdx, 0, {
                    title: 'Vis flere stedsnavn',
                    category: 'Stedsnavn',
                    subtitle: `Klikk for å vise ${config.placenames_page_size} flere stedsnavn`,
                    icon: 'o-icon-plus',
                    onClick: () => dispatch(loadMorePlaceNames(searchText, nextPage)),
                    keepDropdownOpen: true,
                });
            }
        }

        return result;
    }, [
        suggestion,
        searchResult.vegobjekttyper,
        searchResult.land,
        searchResult.fylker,
        searchResult.kommuner,
        searchResult.vegkategorier,
        searchResult.riksvegruter,
        searchResult.kontraktsomrader,
        placeNames?.placeNames,
        dispatch,
        isLoggedIn,
        fylkeByNumber,
        searchText,
        config.placenames_page_size,
    ]);
    return dropdownItems;
};

export const useRecentSearches = (recentSearchQueries: DropdownItemData[]) => {
    const dispatch = useDispatch();
    const { data } = useContext(ConfigAndDataContext);
    const { isLoggedIn } = useSelector(stateUser);
    const kommuneByNummer = useMemo(
        () =>
            data.kommuner.reduce(
                (set, k) => ({ ...set, [k.nummer]: k }),
                {} as Record<string, Kommune>
            ),
        [data.kommuner]
    );
    const riksvegruterByEnumid = useMemo(
        () =>
            data.riksvegruter.reduce(
                (set, r) => ({ ...set, [r.enumid]: r }),
                {} as Record<string, Riksvegrute>
            ),
        [data.riksvegruter]
    );
    const vegsystemreferanseParser = useMemo(
        () => createVegrefParser(data.datakatalog.getTypeById(915)),
        [data]
    );

    return useMemo(() => {
        return recentSearchQueries
            .map((query: DropdownItemData) => {
                switch (query.category) {
                    case 'Vegobjekttype':
                        const vegobjekttype = data.datakatalog.vegobjekttyper.find(
                            v => v.navn === query.title
                        );
                        return vegobjekttype
                            ? vegobjekttypeToEntry(vegobjekttype, dispatch, isLoggedIn)
                            : null;
                    case 'Fylke': {
                        const fylke = data.fylker.find(
                            f => `${f.navn} (${f.nummer})` === query.title
                        );
                        return fylke ? fylkeToEntry(fylke, dispatch) : null;
                    }
                    case 'Kommune': {
                        const kommune = data.kommuner.find(
                            k => `${k.navn} (${k.nummer})` === query.title
                        );
                        return kommune
                            ? kommuneToEntry(kommune, dispatch, county =>
                                  data.fylker.find(f => f.nummer === county)
                              )
                            : null;
                    }
                    case 'Land': {
                        const land = data.land.find(l => l.navn === query.title);
                        return land ? landToEntry(land, dispatch) : null;
                    }
                    case 'Riksvegrute': {
                        const riksvegrute = data.riksvegruter.find(
                            r => r.navn === query.title
                        );
                        return riksvegrute
                            ? riksvegruteToEntry(riksvegrute, dispatch)
                            : null;
                    }
                    case 'Vegkategori': {
                        const vegkategori = data.vegkategorier.find(
                            v => v.navn === query.title
                        );
                        return vegkategori
                            ? vegkategoriToEntry(vegkategori, dispatch)
                            : null;
                    }
                    case 'Kontraktsområde': {
                        const kontraktsomrade = data.kontraktsomrader.find(
                            k => k.navn === query.title
                        );
                        return kontraktsomrade
                            ? kontraktsomradeToEntry(kontraktsomrade, dispatch)
                            : null;
                    }
                }

                switch (query.title) {
                    case 'Polygonsøk':
                        const suggestion = createSearchSuggestionPolygon(
                            query.searchText ?? ''
                        );
                        if (suggestion) return suggestionToEntry(suggestion, dispatch);
                        else return null;
                    case 'Nylige søk':
                        return query;
                }

                if (!query.searchText) return null;

                switch (query.subtitle) {
                    case 'Vegsystemreferanse':
                        const vegsystemreferanse = vegsystemreferanseParser(
                            query.searchText.trim()
                        );
                        if (vegsystemreferanse)
                            return suggestionToEntry(
                                createSearchSuggestionVegsystemreferanse(
                                    query.searchText,
                                    vegsystemreferanse
                                ),
                                dispatch
                            );
                        else return null;
                    case 'Veglenkefilter':
                        return suggestionToEntry(
                            createSearchSuggestionLinkQuery(query.searchText),
                            dispatch
                        );
                    case 'Vis vegsystemreferanse for veglenkeposisjon':
                        return suggestionToEntry(
                            createSearchSuggestionVegsystemreferanseFromLink(
                                query.searchText
                            ),
                            dispatch
                        );
                    case 'Koordinater (Nord, Øst)':
                        return suggestionToEntry(
                            createSearchSuggestionCoordinate(query.searchText),
                            dispatch
                        );
                    case 'Vis vegsystemreferanse for koordinat':
                        return suggestionToEntry(
                            createSearchSuggestionVegsystemreferanseFromCoordinate(
                                query.searchText
                            ),
                            dispatch
                        );
                    case 'Vis vegsystemreferanse fra historisk vegreferanse': {
                        const legacyVegreferanse = parseLegacyVegreferanse(
                            query.searchText
                        );
                        return legacyVegreferanse
                            ? suggestionToEntry(
                                  createSearchSuggestionHistoricalVegreferanse(
                                      legacyVegreferanse
                                  ),
                                  dispatch
                              )
                            : null;
                    }
                    case 'Kommunenummer':
                        const kommune = kommuneByNummer[query.searchText];
                        if (kommune)
                            return suggestionToEntry(
                                createSearchSuggestionKommune(query.searchText, kommune),
                                dispatch
                            );
                        else return null;
                    case 'Riksvegrute':
                        const riksvegrute = riksvegruterByEnumid[query.searchText];
                        if (riksvegrute)
                            return suggestionToEntry(
                                createSearchSuggestionRiksvegrute(
                                    query.searchText,
                                    riksvegrute
                                ),
                                dispatch
                            );
                        else return null;
                    case 'Vegobjekt':
                        return suggestionToEntry(
                            createSearchSuggestionVegobjekt(query.searchText),
                            dispatch
                        );
                }

                if (
                    typeof query.subtitle === 'string' &&
                    query.subtitle.match(/Vegobjekttype (\d+)/)
                ) {
                    const vegobjekttype = data.datakatalog.getTypeById(
                        Number.parseInt(query.searchText)
                    );
                    if (vegobjekttype)
                        return suggestionToEntry(
                            createSearchSuggestionVegobjekttype(
                                query.searchText,
                                vegobjekttype,
                                isLoggedIn
                            ),
                            dispatch
                        );
                }

                return null;
            })
            .filter(entry => entry !== null);
    }, [
        recentSearchQueries,
        data,
        dispatch,
        isLoggedIn,
        kommuneByNummer,
        riksvegruterByEnumid,
        vegsystemreferanseParser,
    ]);
};

function prefixComparator<T>(prefix: string, keyExtractor: (data: T) => string) {
    return (a: T, b: T) => {
        const keyA = keyExtractor(a);
        const keyB = keyExtractor(b);
        const prefixA = keyA.toLowerCase().startsWith(prefix.toLowerCase());
        const prefixB = keyB.toLowerCase().startsWith(prefix.toLowerCase());
        if (prefixA && !prefixB) return -1;
        else if (!prefixA && prefixB) return 1;
        else if (prefixA && prefixB) return keyA.localeCompare(keyB);
        return 0;
    };
}

function kommuneToEntry(
    kommune: Kommune,
    dispatch: Dispatch,
    fylkeByNumber: (number: number) => Fylke | undefined
): DropdownItemData {
    return {
        title: `${kommune?.navn} (${kommune?.nummer})`,
        category: 'Kommune',
        subtitle:
            kommune.fylke == null
                ? undefined
                : `Kommune i ${fylkeByNumber(kommune.fylke)?.navn}`,
        onClick: () => {
            dispatch(addKommuneToSearch(kommune));
        },
    };
}

function fylkeToEntry(fylke: Fylke, dispatch: Dispatch): DropdownItemData {
    return {
        title: `${fylke.navn} (${fylke.nummer})`,
        category: 'Fylke',
        subtitle: 'Fylke',
        onClick: () => {
            dispatch(addFylkeToSearch(fylke));
        },
    };
}

export function vegobjekttypeToEntry(
    vegobjekttype: Vegobjekttype,
    dispatch: Dispatch | undefined,
    isLoggedIn: boolean
): DropdownItemData {
    const sensitive = vegobjekttype.sensitiv;
    const excludedFromIndexing = [521, 524, 525, 526, 562, 641, 644, 645, 793].includes(
        vegobjekttype.id
    );
    let subtitle = `Vegobjekttype ${vegobjekttype.id}`;
    if (sensitive && !excludedFromIndexing)
        subtitle += isLoggedIn
            ? ' (Skjermet type)'
            : ' (Skjermet type, krever innlogging)';
    else if (excludedFromIndexing) subtitle += ' (Ikke tilgjengelig i Vegkart)';

    return {
        title: vegobjekttype.navn,
        category: 'Vegobjekttype',
        onClick: dispatch
            ? () => {
                  dispatch(addVegobjekttypeToSearch(vegobjekttype));
              }
            : undefined,
        disabled: excludedFromIndexing || (!isLoggedIn && sensitive),
        subtitle,
    };
}

function riksvegruteToEntry(
    riksvegrute: Riksvegrute,
    dispatch: Dispatch
): DropdownItemData {
    return {
        title: riksvegrute.navn,
        category: 'Riksvegrute',
        subtitle: `EnumID: ${riksvegrute.enumid}`,
        onClick: () => {
            dispatch(addRiksvegruteToSearch(riksvegrute));
        },
    };
}
function vegkategoriToEntry(
    vegkategori: Vegkategori,
    dispatch: Dispatch
): DropdownItemData {
    return {
        title: vegkategori.navn,
        category: 'Vegkategori',
        subtitle: 'Vegkategori',
        onClick: () => {
            dispatch(addVSRToSearch(vegkategori));
        },
    };
}
function kontraktsomradeToEntry(
    kontraktsomrade: Kontraktsomrade,
    dispatch: Dispatch
): DropdownItemData {
    return {
        title: kontraktsomrade.navn,
        category: 'Kontraktsområde',
        subtitle: 'Kontraktsområde',
        onClick: () => {
            dispatch(addKontraktsomradeToSearch(kontraktsomrade));
        },
    };
}

function landToEntry(land: Land, dispatch: Dispatch): DropdownItemData {
    return {
        title: land.navn,
        category: 'Land',
        subtitle: 'Land',
        onClick: () => {
            dispatch(addLandToSearch(land));
        },
    };
}

function stedsnavnToEntry(stedsnavn: Stedsnavn, dispatch: Dispatch): DropdownItemData {
    return {
        title: stedsnavn.name,
        category: 'Stedsnavn',
        subtitle: formatLocationDetails(stedsnavn),
        icon: getLocationIconName(stedsnavn),
        onClick: () => {
            dispatch(setPlacenameMarker(stedsnavn));
        },
    };
}

function suggestionToEntry(
    suggestion: SearchSuggestion,
    dispatch: Dispatch
): DropdownItemData {
    return {
        title: suggestion.title,
        category: 'Forslag',
        subtitle: suggestion.description,
        onClick: () => suggestion.action(dispatch),
        disabled: suggestion.disabled,
        searchText: suggestion.searchText,
        pendingText: suggestion.pendingText,
    };
}
